interface AuditListSelectableDay {
  reportDate: string;
}

interface NextAuditListReportDateSelectionArgs {
  selectedReportDate: string | null;
  visibleDailyAuditRows: AuditListSelectableDay[];
  canSelectSummary: boolean;
  today?: Date;
  storeTimezone?: string | null;
}

const getDatePartsInTimezone = (date: Date, timezone?: string | null) => {
  if (timezone) {
    try {
      const parts = new Intl.DateTimeFormat("en-CA", {
        timeZone: timezone,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      }).formatToParts(date);

      return {
        year: Number(parts.find((part) => part.type === "year")?.value),
        month: Number(parts.find((part) => part.type === "month")?.value),
        day: Number(parts.find((part) => part.type === "day")?.value),
      };
    } catch {
      // Fall back to the browser date when the configured timezone is invalid.
    }
  }

  return {
    year: date.getFullYear(),
    month: date.getMonth() + 1,
    day: date.getDate(),
  };
};

export const getYesterdayReportDate = (today = new Date(), storeTimezone?: string | null) => {
  const dateParts = getDatePartsInTimezone(today, storeTimezone);
  const yesterday = new Date(Date.UTC(dateParts.year, dateParts.month - 1, dateParts.day - 1));

  const year = yesterday.getUTCFullYear();
  const month = String(yesterday.getUTCMonth() + 1).padStart(2, "0");
  const day = String(yesterday.getUTCDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

export const getNextAuditListReportDateSelection = ({
  selectedReportDate,
  visibleDailyAuditRows,
  canSelectSummary,
  today,
  storeTimezone,
}: NextAuditListReportDateSelectionArgs) => {
  const selectedExists = selectedReportDate
    ? visibleDailyAuditRows.some((day) => day.reportDate === selectedReportDate)
    : false;

  if (selectedReportDate === "summary" && canSelectSummary) {
    return null;
  }

  if (selectedExists) {
    return null;
  }

  const yesterdayReportDate = getYesterdayReportDate(today, storeTimezone);
  const yesterdayRow = visibleDailyAuditRows.find((day) => day.reportDate === yesterdayReportDate);

  if (yesterdayRow) {
    return yesterdayRow.reportDate;
  }

  if (canSelectSummary) {
    return "summary";
  }

  return visibleDailyAuditRows[0]?.reportDate ?? null;
};
